import { useCallback } from 'react';
import { useKey } from 'react-use';

export function hasModifier(event: KeyboardEvent): boolean {
  return event.metaKey || event.ctrlKey;
}

export function isUndoShortcut(event: KeyboardEvent): boolean {
  // Some events (modifier-only presses, some extensions) fire without a `key`.
  if (!event.key) return false;
  return (
    event.key.toLowerCase() === 'z' && hasModifier(event) && !event.shiftKey
  );
}

export function isRedoShortcut(event: KeyboardEvent): boolean {
  if (!event.key) return false;
  const cmdShiftZ =
    event.key.toLowerCase() === 'z' && hasModifier(event) && event.shiftKey;
  const ctrlY = event.key.toLowerCase() === 'y' && event.ctrlKey;
  return cmdShiftZ || ctrlY;
}

export function isEditableTarget(event: KeyboardEvent): boolean {
  const target = event.target;

  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement
  ) {
    return true;
  }

  if (target instanceof HTMLElement) {
    const contentEditable = target.getAttribute('contenteditable');
    return contentEditable !== null && contentEditable !== 'false';
  }

  return false;
}

interface UseUndoRedoKeyboardShortcutsArgs {
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

/**
 * Wires Cmd/Ctrl+Z (undo) and Cmd+Shift+Z / Ctrl+Y (redo) to the callbacks.
 * Ignored while focus is in a text input/textarea/contenteditable so native
 * text-editing undo still works there.
 */
export function useUndoRedoKeyboardShortcuts({
  onUndo,
  onRedo,
  canUndo,
  canRedo,
}: UseUndoRedoKeyboardShortcutsArgs) {
  const handleUndo = useCallback(
    (event: KeyboardEvent) => {
      if (isEditableTarget(event)) return;
      if (!canUndo) return;
      event.preventDefault();
      onUndo();
    },
    [canUndo, onUndo]
  );

  const handleRedo = useCallback(
    (event: KeyboardEvent) => {
      if (isEditableTarget(event)) return;
      if (!canRedo) return;
      event.preventDefault();
      onRedo();
    },
    [canRedo, onRedo]
  );

  // Cmd+Z (Mac) or Ctrl+Z (Windows/Linux)
  useKey(isUndoShortcut, handleUndo, { event: 'keydown' }, [handleUndo]);

  // Cmd+Shift+Z (Mac) or Ctrl+Shift+Z / Ctrl+Y (Windows/Linux)
  useKey(isRedoShortcut, handleRedo, { event: 'keydown' }, [handleRedo]);
}
